/*
Purpose: Change one student's attendance status for one session.
*/

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal } from 'react-bootstrap';
import Button from '@mui/material/Button';

import {
  ATTENDANCE_STATUS,
  DISPLAY_STATUS_META,
  EDITABLE_ATTENDANCE_STATUSES,
} from '../constants';
import { deriveStudentStatus } from '../session-model';

/** Label each selectable attendance code using the same wording as the chips. */
const STATUS_OPTIONS = EDITABLE_ATTENDANCE_STATUSES.map((code) => {
  const status = deriveStudentStatus({ attendance_status: code });
  return { value: String(code), label: DISPLAY_STATUS_META[status].label };
});

export default function EditStatusModal({ show, student, isSubmitting, onCancel, onSave }) {
  const [selected, setSelected] = useState('');
  const [error, setError] = useState('');

  // Re-seed the field whenever a different student is opened.
  useEffect(() => {
    if (show && student) {
      const current = student.attendanceStatus;
      setSelected(
        EDITABLE_ATTENDANCE_STATUSES.includes(current)
          ? String(current)
          : String(ATTENDANCE_STATUS.PENDING)
      );
      setError('');
    }
  }, [show, student]);

  function handleSubmit(event) {
    event.preventDefault();
    if (selected === '') {
      setError('Status is required.');
      return;
    }
    onSave(parseInt(selected, 10));
  }

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Edit Status</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <span className="text-muted">Student</span>
            <div className="fw-bold">{student ? student.name : '—'}</div>
          </div>

          <h6 className="form-lable required-field">Status</h6>
          <Form.Select
            value={selected}
            onChange={(event) => {
              setSelected(event.target.value);
              setError('');
            }}
            className={error ? 'input-error' : ''}
          >
            <option value="">---Select---</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          {error ? <span className="error">{error}</span> : null}
        </Modal.Body>

        <Modal.Footer className="pt-0">
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              className="primary-filled"
              disabled={isSubmitting}
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

EditStatusModal.propTypes = {
  show: PropTypes.bool.isRequired,
  student: PropTypes.shape({
    name: PropTypes.string,
    attendanceStatus: PropTypes.number,
  }),
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

EditStatusModal.defaultProps = {
  student: null,
  isSubmitting: false,
};
